import Box from '@material-ui/core/Box';
import Alert from '@material-ui/lab/Alert';
import { Color } from '@material-ui/lab/Alert/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Account } from '../hooks/useAccount';
import { dateFormat } from '../styles';
import { Product, ProductReferral } from '../swagger';
import { isHealthCheckup } from '../utils/isHealthCheckup';

const severities: Record<ProductReferral.StatusEnum, Color> = {
  [ProductReferral.StatusEnum.Pending]: 'info',
  [ProductReferral.StatusEnum.Active]: 'success',
  [ProductReferral.StatusEnum.Sampled]: 'info',
  [ProductReferral.StatusEnum.Completed]: 'success',
  [ProductReferral.StatusEnum.Failed]: 'error',
  [ProductReferral.StatusEnum.Cancelled]: 'info',
};

/**
 * We date the referral only where the date is about the referral itself. Once
 * the blood has been drawn the send date is history, and while it is still
 * being made there is nothing to date yet.
 */
const showsSentDate: ProductReferral.StatusEnum[] = [ProductReferral.StatusEnum.Active];

const showsCity: ProductReferral.StatusEnum[] = [
  ProductReferral.StatusEnum.Pending,
  ProductReferral.StatusEnum.Active,
];

/**
 * Whether the referral was issued, is still usable, or lapsed is logistics: a
 * company that bought forty checkups has every reason to see which ones got
 * used. That the person has since given blood, or that their results are in,
 * describes the care they received rather than the referral — so those two
 * stay with the person the checkup is for.
 */
const careStatuses: ProductReferral.StatusEnum[] = [
  ProductReferral.StatusEnum.Sampled,
  ProductReferral.StatusEnum.Completed,
];

interface OrderProductReferralProps {
  product: Product;
}

/**
 * The e-referral behind a health checkup: whether it can be used at a lab,
 * until when, and which town was picked.
 *
 * `GET /account` returns every order the caller owns *or pays for*, so a
 * company payer receives its employees' checkup products too. It gets the
 * logistics — issued, usable, lapsed — but not `careStatuses`, and not the
 * instructions, which are written to the patient in the second person.
 */
export const OrderProductReferral: React.FC<OrderProductReferralProps> = ({
  product,
}) => {
  // The locale comes from react-intl rather than the Settings container: the
  // date sits inside a translated sentence, so it must follow the same locale
  // that sentence was rendered in.
  const { locale } = useIntl();
  const { account } = Account.useContainer();
  const referral = product.referral;
  const isOwnCheckup = !!account?.id && account.id === product.account?.id;

  if (!isHealthCheckup(product.type) || !referral) {
    return null;
  }

  const { status, createdAt, city } = referral;

  if (!isOwnCheckup && careStatuses.includes(status)) {
    return null;
  }

  const sent =
    showsSentDate.includes(status) &&
    createdAt &&
    DateTime.fromISO(createdAt, { zone: 'utc' }).isValid
      ? DateTime.fromISO(createdAt, { zone: 'utc' }).toLocaleString({
          ...dateFormat,
          locale,
        })
      : undefined;

  return (
    <Box pb={2}>
      <Alert severity={severities[status]}>
        <AlertTitle>
          {/* "Din remiss" is addressed to the patient. A payer is reading
              about someone else's, so they get the third-person wording. */}
          <FormattedMessage
            id={
              isOwnCheckup
                ? `referral.status.${status}`
                : `referral.other.status.${status}`
            }
          />
        </AlertTitle>
        {isOwnCheckup && <FormattedMessage id={`referral.body.${status}`} />}
        {!!sent && (
          <Box pt={1}>
            <strong>
              <FormattedMessage id="referral.sent_at" values={{ date: sent }} />
            </strong>
          </Box>
        )}
        {showsCity.includes(status) && !!city && (
          <Box>
            <FormattedMessage id="referral.city" values={{ city }} />
          </Box>
        )}
      </Alert>
    </Box>
  );
};
